import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  useTheme,
} from '@mui/material';
import {
  School as SchoolIcon,
  PlayCircle as PlayCircleIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { brand } from '../../config/brand';

// Extract YouTube video ID from various URL formats
const getYouTubeId = (url: string): string => {
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=)([^&]+)/);
  return match ? match[1] : '';
};

interface VideoTutorial {
  title: string;
  description: string;
  url: string;
}

const videoTutorials: VideoTutorial[] = [
  {
    title: `Installing & Launching ${brand.fullProductName}`,
    description: `Learn how to install ${brand.fullProductName} on your system and launch it for the first time.`,
    url: 'https://youtu.be/RhofCrIzn_g',
  },
  {
    title: 'Pairing Autopilot using your API key',
    description: 'Step-by-step guide to pairing your Autopilot installation with your account using an API key.',
    url: 'https://www.youtube.com/watch?v=bcSE2tNqQEA',
  },
  {
    title: 'Configuring your Agent',
    description: 'Learn how to configure your Autopilot agent for your specific project needs.',
    url: 'https://youtu.be/aivyYkS9j6E',
  },
];

const TrainingPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: brand.gradients.hero,
          color: 'white',
          py: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'rgba(255,255,255,0.2)',
              color: 'white',
              px: 2,
              py: 1,
              borderRadius: 2,
              fontSize: '0.875rem',
              mb: 3,
            }}>
              <SchoolIcon fontSize="small" />
              Training Center
            </Box>
            <Typography
              variant="h2"
              component="h1"
              fontWeight="bold"
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                mb: 2,
              }}
            >
              Learn {brand.fullProductName}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                maxWidth: 600,
                mx: 'auto',
                lineHeight: 1.6,
              }}
            >
              Video tutorials and guides to help you get the most out of {brand.productName}.
              From quick start to advanced techniques.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Video Tutorials Section */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Typography variant="h4" fontWeight="bold" textAlign="center" mb={2}>
          Video Tutorials
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center" mb={4}>
          Watch step-by-step guides to get started with {brand.fullProductName}
        </Typography>

        <Grid container spacing={4}>
          {videoTutorials.map((video, index) => {
            const videoId = getYouTubeId(video.url);
            return (
              <Grid item xs={12} md={6} key={index}>
                <Card sx={{ height: '100%' }}>
                  <Box
                    sx={{
                      position: 'relative',
                      paddingTop: '56.25%', // 16:9 aspect ratio
                      bgcolor: 'black',
                    }}
                  >
                    <Box
                      component="iframe"
                      src={`https://www.youtube.com/embed/${videoId}`}
                      title={video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 0,
                      }}
                    />
                  </Box>
                  <CardContent>
                    <Typography variant="h6" fontWeight="600" gutterBottom>
                      {video.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {video.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Subscribe CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="body1" color="text.secondary" mb={2}>
            Subscribe to our YouTube channel for more tutorials
          </Typography>
          <Button
            variant="contained"
            size="large"
            href={brand.links.youtube}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<PlayCircleIcon />}
          >
            YouTube Channel
          </Button>
        </Box>
      </Container>

      {/* Resources Section */}
      <Box sx={{ bgcolor: theme.palette.mode === 'light' ? 'grey.50' : 'background.paper', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight="bold" textAlign="center" mb={4}>
            Additional Resources
          </Typography>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Documentation
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Complete API reference and usage guides on NPM.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  href={brand.links.npm}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Docs
                </Button>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ p: 3, textAlign: 'center', height: '100%' }}>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  Contact Support
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Need help? Reach out to our team directly.
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  component={RouterLink}
                  to="/contact"
                >
                  Get Help
                </Button>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default TrainingPage;
